import { useEffect, useRef, type RefObject } from "react";

import { dragKindOf } from "@/agent/chat";

import { setLipDrag } from "./lipStore";

/** Devices that can drag files in; a phone never raises the lip. */
const DRAG_POINTER = "(hover: hover) and (pointer: fine)";
/** Dragover fires every ~50ms while a drag is over the page; this long without one, it ended. */
const SETTLE_MS = 150;

/**
 * Tracks what the chat takes being dragged over the page, into the lip store: files from outside
 * the window, or an application document (H2's drag type), and whether it is over `lip`. Other
 * drags (text, links, images from this page) never count. Only the lip handles the drop; a drop
 * anywhere else keeps the browser's default. A drop, Esc, or leaving the window ends it.
 */
export function usePageFileDrag(lip: RefObject<HTMLElement>, enabled: boolean): void {
  const active = useRef(enabled);
  active.current = enabled;

  useEffect(() => {
    const pointer = window.matchMedia(DRAG_POINTER);
    // A drag that started in this page; Chrome lists an <img> dragged here as "Files".
    let fromPage = false;
    let settle: number | undefined;

    const end = () => {
      window.clearTimeout(settle);
      setLipDrag(null);
    };
    const onStart = () => {
      fromPage = true;
    };
    const onEnd = () => {
      fromPage = false;
      end();
    };
    const onOver = (event: DragEvent) => {
      const kind = dragKindOf(event.dataTransfer?.types ?? []);
      if (!kind || (kind === "files" && fromPage) || !pointer.matches || !active.current) return;
      // Each dragover names what is under the pointer, so crossing the lip's parts never flickers.
      setLipDrag({ kind, overLip: !!lip.current?.contains(event.target as Node) });
      window.clearTimeout(settle);
      settle = window.setTimeout(end, SETTLE_MS);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") end();
    };

    window.addEventListener("dragstart", onStart);
    window.addEventListener("dragend", onEnd);
    window.addEventListener("dragenter", onOver);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", end);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      end();
      window.removeEventListener("dragstart", onStart);
      window.removeEventListener("dragend", onEnd);
      window.removeEventListener("dragenter", onOver);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", end);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lip]);
}
